import { KeyRound, Minus, Plus, Send, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

interface OrderItem {
  id: string;
  line_no: number;
  item_kind: 'product' | 'service' | 'kit' | 'exception';
  from_kit_component?: boolean;
  kit_id?: string | null;
  item_code: string;
  item_name: string;
  quantity: string;
  unit_price_cents: string;
  total_cents: string;
  is_price_overridden?: boolean;
  original_unit_price_cents?: string | null;
  /** Stage E — kit_items row that priced this line. */
  kit_item_id?: string | null;
  kit_item_price_override_cents?: string | null;
  kit_breakdown_lines?: KitBreakdownLine[];
  /** Phase 8 — lot captured at send-to-cashier time / whether one is still needed. */
  vaccination_lot_number?: string | null;
  requires_vaccination_lot?: boolean;
}

interface KitBreakdownLine {
  item_kind: string;
  item_code: string;
  item_name: string;
  quantity: string;
  unit_price_cents: string;
  total_cents: string;
}

interface OrderLinesPanelProps {
  items: readonly OrderItem[];
  total_cents: string;
  subtotal_cents: string;
  tax_cents: string;
  clinical_notes: string;
  is_editable: boolean;
  is_busy: boolean;
  is_saving_notes: boolean;
  send_disabled_reason: string | null;
  can_request_price_override?: boolean;
  onUpdateQty: (item_id: string, next_qty: string) => void | Promise<void>;
  onRemove: (item_id: string) => void | Promise<void>;
  onUpdateNotes: (notes: string) => void | Promise<void>;
  onSend: () => void | Promise<void>;
  onRequestPriceOverride?: (item_id: string) => void;
}

export function OrderLinesPanel(props: OrderLinesPanelProps): JSX.Element {
  const { t } = useTranslation();
  const grouped_items = groupOrderItems(props.items);

  const onChangeQty = (item: OrderItem, delta: number): void => {
    const current = Number(item.quantity);
    if (Number.isNaN(current)) return;
    const next = Math.max(0.001, Math.round((current + delta) * 1000) / 1000);
    void props.onUpdateQty(item.id, next.toString());
  };

  const send_disabled =
    props.is_busy || props.is_saving_notes || props.send_disabled_reason !== null;

  return (
    <div className="flex flex-col gap-4">
      <div className="border-border bg-card/40 rounded-md border">
        <header className="border-border flex items-center justify-between gap-2 border-b px-4 py-2">
          <h3 className="text-sm font-semibold tracking-tight">{t('doctor_pos.lines_title')}</h3>
          <Badge variant="muted">
            {t('doctor_pos.lines_count', { count: props.items.length })}
          </Badge>
        </header>
        {props.items.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">
            {t('doctor_pos.lines_empty')}
          </p>
        ) : (
          <ul className="divide-border divide-y">
            {grouped_items.map((group, index) => (
              <li key={`grp-${index}`} className="px-4 py-3 text-sm">
                {group.kit_label ? (
                  <div className="bg-muted/40 mb-2 flex items-center justify-between gap-2 rounded-md px-2 py-1">
                    <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                      {t('doctor_pos.kit_group')}: {group.kit_label}
                    </span>
                    <Badge variant="muted">
                      {t('doctor_pos.lines_count', { count: group.items.length })}
                    </Badge>
                  </div>
                ) : null}
                <ul className="space-y-2">
                  {group.items.map((item) => (
                    <li key={item.id} className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {group.kit_label ? kitComponentLabel(item.item_name) : item.item_name}
                          </p>
                          <p className="text-muted-foreground font-mono text-xs">
                            {item.item_code}
                          </p>
                          {item.is_price_overridden && item.original_unit_price_cents ? (
                            <Badge variant="muted" className="mt-1">
                              {t('doctor_pos.price_override_badge', {
                                original: formatCentsString(item.original_unit_price_cents),
                              })}
                            </Badge>
                          ) : null}
                          {item.kit_item_id ? (
                            <Badge
                              variant="muted"
                              className="mt-1"
                              title={
                                item.kit_item_price_override_cents
                                  ? formatCentsString(item.kit_item_price_override_cents)
                                  : t('doctor_pos.line_badge_kit_priced')
                              }
                            >
                              {t('doctor_pos.line_badge_kit_priced')}
                            </Badge>
                          ) : null}
                          {item.vaccination_lot_number || item.requires_vaccination_lot ? (
                            <Badge
                              variant={item.requires_vaccination_lot ? 'destructive' : 'muted'}
                              className="mt-1"
                              title={item.vaccination_lot_number ?? undefined}
                            >
                              {item.vaccination_lot_number
                                ? t('doctor_pos.line_badge_vaccination_lot', {
                                    lot: item.vaccination_lot_number,
                                  })
                                : t('doctor_pos.line_badge_vaccination_pending')}
                            </Badge>
                          ) : null}
                          {item.item_kind === 'kit' && item.kit_breakdown_lines?.length ? (
                            <ul className="border-border bg-muted/30 mt-2 space-y-1 rounded-md border p-2">
                              {item.kit_breakdown_lines.map((line, idx) => (
                                <li
                                  key={`${item.id}-kit-breakdown-${idx}`}
                                  className="grid grid-cols-[1fr_auto] gap-2 text-xs"
                                >
                                  <span className="min-w-0">
                                    <span className="text-muted-foreground font-mono">
                                      {line.item_code}
                                    </span>{' '}
                                    {line.item_name}
                                  </span>
                                  <span className="text-muted-foreground whitespace-nowrap font-mono">
                                    {line.quantity} x {formatCentsString(line.unit_price_cents)} ={' '}
                                    {formatCentsString(line.total_cents)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                          {item.item_kind === 'kit' && !item.kit_breakdown_lines?.length ? (
                            <p className="border-border bg-muted/20 text-muted-foreground mt-2 rounded-md border border-dashed p-2 text-xs">
                              Sin componentes configurados para este kit
                            </p>
                          ) : null}
                        </div>
                        <span className="font-mono">{formatCentsString(item.total_cents)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => onChangeQty(item, -1)}
                            disabled={!props.is_editable || props.is_busy}
                            aria-label={t('doctor_pos.qty_decrease')}
                          >
                            <Minus className="h-4 w-4" aria-hidden />
                          </Button>
                          <Input
                            className="h-9 w-20 text-center font-mono"
                            value={item.quantity}
                            onChange={(e) => {
                              void props.onUpdateQty(item.id, e.target.value);
                            }}
                            disabled={!props.is_editable || props.is_busy}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => onChangeQty(item, +1)}
                            disabled={!props.is_editable || props.is_busy}
                            aria-label={t('doctor_pos.qty_increase')}
                          >
                            <Plus className="h-4 w-4" aria-hidden />
                          </Button>
                        </div>
                        <span className="text-muted-foreground font-mono text-xs">
                          {formatCentsString(item.unit_price_cents)} x
                        </span>
                        <div className="flex items-center gap-1">
                          {props.can_request_price_override &&
                          props.onRequestPriceOverride &&
                          !item.from_kit_component ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => props.onRequestPriceOverride?.(item.id)}
                              disabled={!props.is_editable || props.is_busy}
                              aria-label={t('doctor_pos.price_override_action')}
                              title={t('doctor_pos.price_override_action')}
                            >
                              <KeyRound className="h-4 w-4" aria-hidden />
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              void props.onRemove(item.id);
                            }}
                            disabled={!props.is_editable || props.is_busy}
                            aria-label={t('doctor_pos.remove_line')}
                          >
                            <Trash2 className="text-destructive h-4 w-4" aria-hidden />
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-border bg-card/40 rounded-md border p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('doctor_pos.subtotal')}</span>
          <span className="font-mono">{formatCentsString(props.subtotal_cents)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('doctor_pos.tax')}</span>
          <span className="font-mono">{formatCentsString(props.tax_cents)}</span>
        </div>
        <div className="border-border mt-3 flex items-center justify-between border-t pt-3 text-2xl font-semibold">
          <span>{t('doctor_pos.total')}</span>
          <span className="font-mono">{formatCentsString(props.total_cents)}</span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <label
            htmlFor="doctor-notes"
            className="text-muted-foreground text-xs font-medium uppercase tracking-wide"
          >
            {t('doctor_pos.clinical_notes')}
          </label>
          {props.is_saving_notes ? (
            <span className="text-muted-foreground text-xs" role="status" aria-live="polite">
              {t('doctor_pos.notes_saving')}
            </span>
          ) : null}
        </div>
        <textarea
          id="doctor-notes"
          rows={3}
          value={props.clinical_notes}
          onChange={(e) => {
            void props.onUpdateNotes(e.target.value);
          }}
          disabled={!props.is_editable || props.is_busy}
          className="border-input bg-background focus:ring-ring flex w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2"
          placeholder={t('doctor_pos.clinical_notes_placeholder')}
        />
      </div>

      <Button
        type="button"
        size="lg"
        onClick={() => {
          void props.onSend();
        }}
        disabled={send_disabled}
      >
        <Send className="h-4 w-4" aria-hidden />
        <span>{t('doctor_pos.send_to_cashier')}</span>
      </Button>
      {props.send_disabled_reason ? (
        <p className="text-muted-foreground text-xs">{props.send_disabled_reason}</p>
      ) : null}
    </div>
  );
}

function extractKitLabel(item_name: string): string | null {
  const idx = item_name.indexOf(' - ');
  if (idx <= 0) return null;
  return item_name.slice(0, idx).trim() || null;
}

function kitComponentLabel(item_name: string): string {
  const idx = item_name.indexOf(' - ');
  if (idx <= 0) return item_name;
  return item_name.slice(idx + 3).trim() || item_name;
}

function groupOrderItems(
  items: readonly OrderItem[],
): { kit_label: string | null; items: OrderItem[] }[] {
  const groups: { kit_label: string | null; items: OrderItem[] }[] = [];
  for (const item of items) {
    if (item.from_kit_component && item.kit_id) {
      const label = extractKitLabel(item.item_name);
      const last = groups[groups.length - 1];
      if (last?.kit_label === label) {
        last.items.push(item);
        continue;
      }
      groups.push({ kit_label: label, items: [item] });
      continue;
    }
    groups.push({ kit_label: null, items: [item] });
  }
  return groups;
}
